import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import {
  Bell,
  TrendingUp,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Package,
} from "lucide-react";

export interface Notification {
  id: string;
  type: "listing" | "price" | "distribution" | "success" | "warning" | "info";
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  link?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (
    notification: Omit<Notification, "id" | "timestamp" | "read">
  ) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

// Sample initial notifications
const initialNotifications: Notification[] = [
  {
    id: "1",
    type: "listing",
    title: "New Asset Listed",
    message: "Q7 BER Airport Digital Cluster is now available for investment",
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    read: false,
    link: "/asset/skyland-q7-ber",
  },
  {
    id: "2",
    type: "distribution",
    title: "Distribution Received",
    message: "You received $175.00 from Hyde Heights | UAE",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: false,
  },
  {
    id: "3",
    type: "price",
    title: "Price Alert",
    message: "Amazon Reforestation Project token price increased by 8.5%",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
  },
  {
    id: "4",
    type: "success",
    title: "Investment Confirmed",
    message: "Your investment of $5,000 in Solar Farm has been confirmed",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
    read: true,
  },
];

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] =
    useState<Notification[]>(initialNotifications);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
      const newNotification: Notification = {
        ...notification,
        id: Date.now().toString(),
        timestamp: new Date(),
        read: false,
      };
      setNotifications((prev) => [newNotification, ...prev]);
    },
    []
  );

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const getNotificationIcon = (type: Notification["type"]) => {
  switch (type) {
    case "listing":
      return <Package className="w-5 h-5 text-vestn-info" />;
    case "price":
      return <TrendingUp className="w-5 h-5 text-primary" />;
    case "distribution":
      return <DollarSign className="w-5 h-5 text-vestn-active" />;
    case "success":
      return <CheckCircle className="w-5 h-5 text-vestn-active" />;
    case "warning":
      return <AlertCircle className="w-5 h-5 text-vestn-pending" />;
    case "info":
    default:
      return <Bell className="w-5 h-5 text-muted-foreground" />;
  }
};

export const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};
